import {
  darkenColor,
  lightenColor,
  mixColors,
  ThemeColors,
  ThemeDefinition,
  ThemeVariables,
  LayoutColors,
  CalendarColors,
  RsvpColors,
  SurfaceColors,
  ControlColors
} from './themeUtils'

const brandBlue = '#0066FF'
const brandPink = '#E8008F'
const brandPurple = '#8609ce'

const sharedColors = (themeMode: 'light' | 'dark' = 'light') => {
  return {
    'brand-blue': brandBlue,
    'brand-pink': brandPink,
    'brand-purple': brandPurple,
    // Defrag/ideas surfaces are purple-heavy; the base brand purple is too
    // dark to read on a dark calendar canvas, so dark themes get a lighter
    // lavender. Scoped to defrag — does not shift `brand-purple` itself.
    'defrag-purple': themeMode === 'light' ? brandPurple : '#a78bfa'
  }
}

const standardStateColors = (): { error; info; success; warning } => {
  return {
    error: '#D03060',
    info: '#3b3f4f',
    success: '#4CAF50',
    warning: '#FB8C00'
  }
}

const standardLayoutColors = (
  baseColors: SurfaceColors,
  themeMode: 'light' | 'dark'
): LayoutColors => {
  const mobileLayoutColors = {
    'mobile-tabs-background-start':
      themeMode === 'light'
        ? darkenColor(baseColors['surface-000'], 0.05)
        : baseColors['surface-200'],
    'mobile-tabs-background-end':
      themeMode === 'light' ? baseColors['surface-000'] : baseColors['surface-100'],
    'mobile-application-bar-background':
      themeMode === 'light' ? baseColors['surface-000'] : baseColors['surface-100'],
    'mobile-application-bar-border':
      themeMode === 'light' ? baseColors['surface-border-000'] : baseColors['surface-border-100'],
    'mobile-screen-background':
      themeMode === 'light' ? baseColors['surface-000'] : baseColors['surface-100'],
    'mobile-preference-group-background':
      themeMode === 'light'
        ? baseColors['surface-000']
        : lightenColor(baseColors['surface-200'], 0.04),
    'mobile-navigation-note-preview-background':
      themeMode === 'light' ? baseColors['surface-100'] : baseColors['surface-200'],
    'mobile-navigation-note-preview-border':
      themeMode === 'light' ? baseColors['surface-border-100'] : baseColors['surface-border-200'],
    'mobile-search-field-background':
      themeMode === 'light'
        ? baseColors['surface-100']
        : lightenColor(baseColors['surface-200'], 0.04)
  }

  return {
    ...mobileLayoutColors,
    'left-navigation-background': baseColors['surface-100'],
    'left-navigation-border': baseColors['surface-border-100'],
    'top-navigation-background': baseColors['surface-200'],
    'top-navigation-border': baseColors['surface-border-200'],
    'note-background': baseColors['surface-000'],
    'note-border': baseColors['surface-border-000'],
    'right-panel-background': baseColors['surface-100'],
    'right-panel-border': baseColors['surface-border-100']
  }
}

const standardControlColors = (
  baseColors: SurfaceColors,
  themeMode: 'light' | 'dark'
): ControlColors => {
  return {
    'control-on-surface-000':
      themeMode === 'light'
        ? darkenColor(baseColors['surface-000'], 0.05)
        : lightenColor(baseColors['surface-000'], 0.05),
    'control-on-surface-100':
      themeMode === 'light'
        ? darkenColor(baseColors['surface-100'], 0.05)
        : lightenColor(baseColors['surface-100'], 0.05),
    'control-on-surface-200':
      themeMode === 'light'
        ? darkenColor(baseColors['surface-200'], 0.05)
        : lightenColor(baseColors['surface-200'], 0.05),
    'control-on-surface-300':
      themeMode === 'light'
        ? darkenColor(baseColors['surface-300'], 0.05)
        : lightenColor(baseColors['surface-300'], 0.05)
  }
}

const standardRsvpColors = (baseColors: Partial<ThemeColors>): RsvpColors => {
  if (
    !baseColors.success ||
    !baseColors.warning ||
    !baseColors.error ||
    !baseColors['highlight-grey'] ||
    !baseColors.primary
  ) {
    throw new Error('Success, warning, error, highlight-grey, and primary colors are required')
  }
  return {
    'rsvp-background': baseColors['highlight-grey'],
    'rsvp-accepted': baseColors.success,
    'rsvp-tentative': baseColors.warning,
    'rsvp-rejected': baseColors.error
  }
}

const standardCalendarColors = (
  baseColors: SurfaceColors,
  themeMode: 'light' | 'dark'
): CalendarColors => {
  return {
    'calendar-day-border':
      themeMode === 'dark'
        ? baseColors['surface-border-200']
        : lightenColor(baseColors['surface-border-100'], 0.55),
    'calendar-time-border': baseColors['surface-border-000'],
    'calendar-group-footer-background': baseColors['surface-000'],
    'calendar-group-footer-border': baseColors['surface-border-000'],
    'calendar-time-selection-background': baseColors['surface-text-000'],
    'calendar-time-selection-border': baseColors['surface-text-000'],
    // Need to reall audit all calendar colors and make this extensible but easy to theme

    'calendar-event-default': brandBlue,
    'calendar-event-notable':
      themeMode === 'dark'
        ? mixColors(brandPink, brandPurple, 0.4)
        : mixColors(brandPink, brandPurple, 0.4),
    'calendar-event-focus':
      themeMode === 'dark' ? baseColors['surface-200'] : baseColors['surface-000']
  }
}

const standardNoteColors = (
  baseColors: SurfaceColors & Pick<ThemeColors, 'primary' | 'highlight-grey'>,
  themeMode: 'light' | 'dark'
) => {
  if (themeMode === 'dark') {
    return {
      'note-mention-text': '#ffffff',
      'note-mention-border': baseColors['highlight-grey'],

      // AI-generated content wash. A soft periwinkle-indigo, distinct from the
      // notable pink and default blue, rendered at low opacity in note.css.
      // Lifted for dark themes so it still reads over the near-black page.
      'note-ai-highlight': '#9aa0ec',

      'note-code-background': '#2d2f41',
      'note-code-border': '#4b4d5f',

      'note-widget-weekly': '#8a6717',
      'note-widget-adhoc': baseColors['highlight-grey'],
      'note-widget-event': '#233264',
      'note-widget-person': '#2a4b2b',

      // Card/tab surface sits well up the scale (55% toward surface-100) so it
      // lifts clearly above the near-black page; the chip rides a hair lighter
      // still (3% white over surface-100).
      'note-mention-preview-surface': mixColors(
        baseColors['surface-000'],
        baseColors['surface-100'],
        0.55
      ),
      'note-mention-chip-surface': mixColors(baseColors['surface-100'], '#ffffff', 0.03),

      'note-list-background': baseColors['surface-200'],
      'note-list-border': lightenColor(baseColors['surface-border-100'], 0.03),

      'note-details-background': baseColors['surface-200'],
      'note-details-border': lightenColor(baseColors['surface-border-100'], 0.3),

      'note-table-background': baseColors['surface-200'],
      'note-table-border': lightenColor(baseColors['surface-border-100'], 0.1),
      'note-table-header-background': baseColors['surface-200'],
      // Floating table toolbars: the lightest elevated surface in dark theme so
      // they read as a raised panel, not a muddy block.
      'note-toolbar-background': baseColors['surface-200'],
      'note-table-active': baseColors.primary,
      'note-table-active-border': darkenColor(baseColors.primary, 0.3),
      'note-table-grip-background': darkenColor(baseColors['surface-200'], 0.2)
    }
  }

  return {
    'note-mention-text': '#ffffff',
    'note-mention-border': baseColors['surface-border-100'],

    // AI-generated content wash. A soft periwinkle-indigo, distinct from the
    // notable pink and default blue, rendered at low opacity in note.css.
    'note-ai-highlight': '#6b74d6',

    'note-code-background': '#2d2f41',
    'note-code-border': '#4b4d5f',

    'note-widget-weekly': '#d79459',
    'note-widget-adhoc': '#999ba9',
    'note-widget-event': '#5871c3',
    'note-widget-person': '#649366',

    // Card/tab surface sits a hair off the page (25% toward surface-100) so the
    // card is distinct but lighter than a full surface-100 fill; the chip/tab
    // bar sit a touch darker still (32%) to separate from the lighter page.
    'note-mention-preview-surface': mixColors(
      baseColors['surface-000'],
      baseColors['surface-100'],
      0.25
    ),
    'note-mention-chip-surface': mixColors(
      baseColors['surface-000'],
      baseColors['surface-100'],
      0.32
    ),

    'note-list-border': baseColors['surface-border-100'],
    // Task-list fill: a lighter surface in light themes so the pill reads softer
    // against the note (dark keeps surface-200). Rendered at 0.8 opacity + border.
    'note-list-background': baseColors['surface-100'],

    'note-details-border': baseColors['surface-border-100'],
    'note-details-background': baseColors['surface-200'],

    'note-table-background': '#ffffff',
    'note-table-border': lightenColor(baseColors['surface-border-100'], 0.3),
    // Header fill sits a touch lighter than the gridlines so headers read as a
    // subtle band without competing with the (already light) grid.
    'note-table-header-background': lightenColor(baseColors['surface-border-100'], 0.55),
    // Floating table toolbars: near-white so they read as a clean floating card
    // over the note (the surface-200 grey read too dark here). Border + shadow do
    // the separating, same as the app's dropdown menus.
    'note-toolbar-background': baseColors['surface-000'],
    'note-table-active': lightenColor(baseColors.primary, 0.1),
    'note-table-active-border': lightenColor(baseColors.primary, 0.1),
    'note-table-grip-background': lightenColor(baseColors['surface-200'], 0.5)
  }
}

const standardLightThemeVariables = (): ThemeVariables => {
  return {
    'note-list-background-opacity': 0.8,
    'note-details-background-opacity': 0.8,
    'note-table-background-opacity': 1,
    'note-mention-card-shadow': '0 2px 8px rgba(var(--v-theme-surface-text-000), 0.05)',
    'floating-shadow': '0 0 10px rgba(0, 0, 0, 0.1)',
    'shadow-intensity': 0.05,
    'theme-highlight-glossy':
      'linear-gradient(155deg, rgba(var(--v-theme-highlight), .75) 0%, rgba(var(--v-theme-highlight), .9) 60%, rgba(var(--v-theme-highlight), .75) 100%)',
    'theme-overlay-multiplier': 0.9,
    'calendar-past-brightness': 0.7,
    'calendar-declined-brightness': 0.7,
    'calendar-hover-brightness': 1.3,
    'calendar-event-gradient-intensity': 0.2,
    'calendar-background-event-minimized-brightness': 1
  }
}

const standardDarkThemeVariables = (): ThemeVariables => {
  return {
    'note-list-background-opacity': 0.8,
    'note-details-background-opacity': 0.8,
    'note-table-background-opacity': 0.5,
    // A surface-text-based shadow reads as a halo on the near-black page, so drop
    // it; the elevated surface already lifts the card above the page in dark.
    'note-mention-card-shadow': 'none',
    'floating-shadow': '0 0 10px 3px rgba(0, 0, 0, 0.2)',
    'shadow-intensity': 0.2,
    'theme-highlight-glossy':
      'linear-gradient(155deg, rgba(var(--v-theme-highlight), 0.65) 0%, rgba(var(--v-theme-highlight), .75) 60%, rgba(var(--v-theme-highlight), 0.75) 100%)',
    'theme-overlay-multiplier': 0.8,
    'calendar-past-brightness': 0.4,
    'calendar-declined-brightness': 0.4,
    'calendar-hover-brightness': 1.4,
    'calendar-event-gradient-intensity': 1,
    'calendar-background-event-minimized-brightness': 0.9
  }
}

export const shadowThemeColors = (): ThemeColors => {
  const baseColors = {
    surface: '#141312',
    'surface-000': '#141312',
    'surface-100': '#191918',
    'surface-200': '#1E1E1C',
    'surface-300': '#302e2e',
    'surface-border-000': '#201f1d',
    'surface-border-100': '#242220',
    'surface-border-200': '#2b2926',
    'surface-border-300': '#323130',
    'surface-text-000': '#ffffff',

    highlight: darkenColor(brandPurple, 0.2),
    'highlight-grey': '#323130',
    'highlight-grey-variant': '#323130', // currently the same as highlight-grey
    primary: brandBlue,
    secondary: brandPink,
    ...standardStateColors()
  }

  return {
    ...baseColors,
    ...sharedColors('dark'),
    ...standardLayoutColors(baseColors, 'dark'),
    ...standardControlColors(baseColors, 'dark'),
    ...standardCalendarColors(baseColors, 'dark'),
    ...{
      'calendar-group-footer-background': '#000000'
    },
    ...standardRsvpColors(baseColors),
    ...standardNoteColors(baseColors, 'dark'),

    'hotkey-background': '#000000',
    'hotkey-border': '#111111'
  }
}

export const shadowThemeVariables = (): ThemeVariables => {
  return standardDarkThemeVariables()
}

export const quietFjordThemeColors = (): ThemeColors => {
  const baseColors = {
    surface: '#1a1d22',
    'surface-000': '#1a1d22',
    'surface-100': '#20242b',
    'surface-200': '#262b33',
    'surface-300': '#333b45',
    'surface-border-000': '#2b3038',
    'surface-border-100': '#2E363E',
    'surface-border-200': '#363F4A',
    'surface-border-300': '#404A57',
    'surface-text-000': '#ffffff',

    highlight: darkenColor(brandPurple, 0.15),
    'highlight-grey': '#3a4350',
    'highlight-grey-variant': '#3f4957',
    primary: brandBlue,
    secondary: brandPink,
    ...standardStateColors()
  }

  return {
    ...baseColors,
    ...sharedColors('dark'),
    ...standardLayoutColors(baseColors, 'dark'),
    ...standardControlColors(baseColors, 'dark'),
    ...standardCalendarColors(baseColors, 'dark'),
    ...{
      'calendar-group-footer-background': '#13171d'
    },
    ...standardRsvpColors(baseColors),
    ...standardNoteColors(baseColors, 'dark'),

    'hotkey-background': '#11151b',
    'hotkey-border': '#202a36'
  }
}

export const quietFjordThemeVariables = (): ThemeVariables => {
  return standardDarkThemeVariables()
}

export const zenThemeColors = (): ThemeColors => {
  const baseColors = {
    // Canvas lift: base surface nudged toward white; the rest of the surface
    // scale is lifted a touch toward white too so the greys read a little
    // lighter to match. Borders unchanged.
    surface: '#FBFBFB',
    'surface-000': '#FBFBFB',
    'surface-100': '#EEEDEB',
    'surface-200': '#EAE9E7',
    'surface-300': '#E2E1DC',
    'surface-border-000': '#E5E5E5',
    'surface-border-100': '#D9D7D3',
    'surface-border-200': '#D3D1CD',
    'surface-border-300': '#D3D2CE',
    'surface-text-000': '#000000',

    highlight: brandPurple,
    'highlight-grey': '#DDDBD7',
    'highlight-grey-variant': '#CFCDC8',
    primary: brandBlue,
    secondary: brandPink,
    ...standardStateColors()
  }

  return {
    ...baseColors,
    ...sharedColors(),
    ...standardLayoutColors(baseColors, 'light'),
    ...standardControlColors(baseColors, 'light'),
    ...standardCalendarColors(baseColors, 'light'),
    ...standardRsvpColors(baseColors),
    ...standardNoteColors(baseColors, 'light'),

    'hotkey-background': '#E0DDD4',
    'hotkey-border': '#D5D3CA'
  }
}

export const zenThemeVariables = (): ThemeVariables => {
  return standardLightThemeVariables()
}

export const mistThemeColors = (): ThemeColors => {
  const baseColors = {
    // Canvas lift: base surface nudged toward white, keeping mist's cool tint;
    // the rest of the surface scale is lifted a touch too so the greys read a
    // little lighter to match. Borders unchanged.
    surface: '#F8FAFC',
    'surface-000': '#F8FAFC',
    'surface-100': '#E6EBF0',
    'surface-200': '#DAE1EA',
    'surface-300': '#CFD6DC',
    'surface-border-000': '#d9e1e9',
    'surface-border-100': '#c8d3da',
    'surface-border-200': '#c3ceda',
    'surface-border-300': '#c2cdd9',
    'surface-text-000': '#000000',

    highlight: brandPurple,
    'highlight-grey': '#d3d9e1',
    'highlight-grey-variant': '#b8c2cf',
    primary: brandBlue,
    secondary: brandPink,
    ...standardStateColors()
  }

  return {
    ...baseColors,
    ...sharedColors(),
    ...standardLayoutColors(baseColors, 'light'),
    ...standardControlColors(baseColors, 'light'),
    ...standardCalendarColors(baseColors, 'light'),
    ...standardRsvpColors(baseColors),
    ...standardNoteColors(baseColors, 'light'),

    'hotkey-background': '#d3d9e1',
    'hotkey-border': '#c1c9d5'
  }
}

export const mistThemeVariables = (): ThemeVariables => {
  return standardLightThemeVariables()
}

export const themeLibrary: ThemeDefinition[] = [
  {
    id: 'zen',
    name: 'Zen', // User facing name
    type: 'light',
    theme: {
      dark: false,
      colors: zenThemeColors(),
      variables: zenThemeVariables()
    }
  },
  {
    id: 'mist',
    name: 'Mist', // User facing name
    type: 'light',
    theme: {
      dark: false,
      colors: mistThemeColors(),
      variables: mistThemeVariables()
    }
  },
  {
    id: 'shadow',
    name: 'Shadow', // User facing name
    type: 'dark',
    theme: {
      dark: true,
      colors: shadowThemeColors(),
      variables: shadowThemeVariables()
    }
  },
  {
    id: 'quiet-fjord',
    name: 'Quiet Fjord', // User facing name
    type: 'dark',
    theme: {
      dark: true,
      colors: quietFjordThemeColors(),
      variables: quietFjordThemeVariables()
    }
  }
  // Add more themes here in the future
]

export const getThemeById = (id: string): ThemeDefinition | undefined => {
  return themeLibrary.find((theme) => theme.id === id)
}

export const getThemesByType = (type: 'light' | 'dark'): ThemeDefinition[] => {
  return themeLibrary.filter((theme) => theme.type === type)
}
